// Time-left estimate for the paged CSV exports.
//
// Throughput is measured as wall-clock elapsed per completed page, which already
// accounts for the fetch pool running several pages at once -- pages/second is
// pages/second regardless of how many are in flight.

/**
 * Estimate the milliseconds left to finish paging.
 *
 * Returns null when there is not enough evidence for an honest number: nothing
 * finished yet, already done, or fewer completions than the pool width. That
 * last one matters because the first wave of pages lands almost together, so a
 * sample of one would divide a full round trip by a single page and overstate
 * the remaining time by the width of the pool.
 *
 * @param {number} completedPages pages finished so far
 * @param {number} totalPages pages in the export
 * @param {number} elapsedMs wall-clock time since the first request went out
 * @param {number} minSamples completions required before estimating
 */
export function estimateTimeLeftMs(
  completedPages: number,
  totalPages: number,
  elapsedMs: number,
  minSamples = 1,
): number | null {
  const remainingPages = totalPages - completedPages;
  if (remainingPages <= 0) return null;
  if (completedPages <= 0 || elapsedMs <= 0) return null;

  // Never withhold an estimate for longer than the export can supply samples: an
  // export of three pages can only ever reach two completions with work left.
  if (completedPages < Math.min(minSamples, totalPages - 1)) return null;

  return (elapsedMs / completedPages) * remainingPages;
}

/**
 * Render an estimate as text an admin can act on. Deliberately coarse -- the
 * estimate is a projection of average throughput, and second-level precision
 * would imply confidence it does not have.
 *
 * @param {number | null} ms estimate from estimateTimeLeftMs
 */
export function formatTimeLeft(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms) || ms <= 0) return '';

  const seconds = Math.ceil(ms / 1000);
  if (seconds <= 10) return 'less than 10 seconds left';
  if (seconds < 60) return `about ${Math.ceil(seconds / 5) * 5} seconds left`;

  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = seconds % 60;

  if (minutes < 60) {
    if (remainderSeconds < 15) return `about ${minutes} min left`;
    if (remainderSeconds < 45) return `about ${minutes}½ min left`;
    return `about ${minutes + 1} min left`;
  }

  const hours = Math.floor(minutes / 60);
  const remainderMinutes = minutes % 60;
  return remainderMinutes === 0
    ? `about ${hours} hr left`
    : `about ${hours} hr ${remainderMinutes} min left`;
}
